import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CookieIcon from "@mui/icons-material/Cookie";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import FacebookIcon from "@mui/icons-material/Facebook";
import MarketingIcon from "@mui/icons-material/Campaign";
import SettingsIcon from "@mui/icons-material/Settings";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  facebook: boolean;
}

const CookieConsent: React.FC = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    facebook: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowConsent(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      initializeTracking(savedPreferences);
    }
  }, []);

  const initializeGoogleAnalytics = useCallback(() => {
    const gaId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
    if (!gaId) return; // Don't load if no ID

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag("js", new Date());
    gtag("config", gaId, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }, []);

  const initializeFacebookPixel = useCallback(() => {
    const pixelId = process.env.REACT_APP_FACEBOOK_PIXEL_ID;
    if (!pixelId) return; // Don't load if no ID

    (function (f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js",
      window.fbq,
      window.fbq,
      window.fbq
    );
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
  }, []);

  const initializeMarketingTracking = useCallback(() => {
    // Add marketing/advertising tracking scripts here
  }, []);

  const initializeTracking = useCallback(
    (prefs: CookiePreferences) => {
      if (prefs.analytics) {
        initializeGoogleAnalytics();
      }

      if (prefs.facebook) {
        initializeFacebookPixel();
      }

      if (prefs.marketing) {
        initializeMarketingTracking();
      }
    },
    [
      initializeFacebookPixel,
      initializeGoogleAnalytics,
      initializeMarketingTracking,
    ]
  );

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      facebook: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    setShowConsent(false);
    initializeTracking(allAccepted);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    setShowConsent(false);
    setShowSettings(false);
    initializeTracking(preferences);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      facebook: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem("cookieConsent", JSON.stringify(onlyNecessary));
    setShowConsent(false);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!showConsent && !showSettings) return null;

  return (
    <>
      {/* Main Cookie Consent Banner */}
      {showConsent && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            bgcolor: "#fff",
            borderTop: "1px solid #eee",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              width: "100%",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <CookieIcon sx={{ color: "#047ABC", mr: 1 }} />
              <Typography
                variant="body1"
                sx={{ color: "#666", fontSize: "0.9rem" }}
              >
                אנו משתמשים בעוגיות כדי לשפר את החוויה שלך באתר.
                <Button
                  variant="text"
                  onClick={() => setShowSettings(true)}
                  sx={{
                    color: "#047ABC",
                    fontWeight: 600,
                    p: 0,
                    minWidth: "auto",
                    textDecoration: "underline",
                  }}
                >
                  למידע נוסף
                </Button>
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                onClick={handleRejectAll}
                size="small"
                sx={{
                  borderColor: "#ccc",
                  color: "#666",
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8rem",
                }}
              >
                דחה
              </Button>
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                size="small"
                sx={{
                  bgcolor: "#047ABC",
                  color: "#fff",
                  fontWeight: 700,
                  px: 2,
                  py: 0.5,
                  fontSize: "0.8rem",
                  "&:hover": { bgcolor: "#035a8c" },
                }}
              >
                קבל הכל
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle
          sx={{ bgcolor: "#f8f9fa", borderBottom: "1px solid #eee" }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SettingsIcon sx={{ color: "#047ABC", mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>
              הגדרות עוגיות
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            בחר איזה סוגי עוגיות אתה רוצה לאפשר. עוגיות הכרחיות תמיד פעילות כדי
            שהאתר יעבוד כראוי.
          </Typography>

          {/* Necessary Cookies */}
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <CookieIcon sx={{ color: "#4caf50", mr: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  עוגיות הכרחיות
                </Typography>
                <Chip label="תמיד פעיל" color="success" size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                עוגיות אלה הכרחיות לפעולת האתר ואי אפשר לכבות אותן. הן כוללות
                עוגיות אבטחה, העדפות שפה ופונקציות בסיסיות של האתר.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Analytics Cookies */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <AnalyticsIcon sx={{ color: "#2196f3", mr: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  עוגיות אנליטיקה
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange("analytics")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                עוגיות אלה עוזרות לנו להבין איך מבקרים משתמשים באתר על ידי איסוף
                מידע אנונימי. זה עוזר לנו לשפר את האתר ולספק חוויה טובה יותר.
              </Typography>
              <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                כולל Google Analytics לניתוח תנועה ושימוש באתר
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* Marketing Cookies */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <MarketingIcon sx={{ color: "#ff9800", mr: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  עוגיות שיווק ופרסום
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.marketing}
                      onChange={() => handlePreferenceChange("marketing")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                עוגיות אלה משמשות לעקוב אחר מבקרים באתרים כדי להציג פרסומות
                רלוונטיות ומותאמות אישית למשתמש.
              </Typography>
              <Alert severity="warning" sx={{ fontSize: "0.875rem" }}>
                כולל פרסומות מותאמות אישית ועקיבה אחר התנהגות משתמש
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* Facebook Pixel */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <FacebookIcon sx={{ color: "#1877f2", mr: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  Facebook Pixel
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.facebook}
                      onChange={() => handlePreferenceChange("facebook")}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Facebook Pixel עוזר לנו למדוד את האפקטיביות של הפרסומות שלנו
                ולבנות קהלים מותאמים אישית לקמפיינים עתידיים.
              </Typography>
              <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                כולל מעקב אחר פעולות משתמש וניתוח ביצועי פרסומות
              </Alert>
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setShowSettings(false)} sx={{ color: "#666" }}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleAcceptSelected}
            sx={{
              bgcolor: "#047ABC",
              color: "#fff",
              fontWeight: 700,
              px: 3,
              "&:hover": { bgcolor: "#035a8c" },
            }}
          >
            שמור הגדרות
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
